import {assert} from 'Asserts';
import {EXTERNAL_FILE_TYPE} from 'constants/PreviewTypeConstants';
import {PredictionJobStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PreSignedStructureInfo} from 'model-hub/scientific-models/structure-prediction/StructurePredictionUtils';
import sortedBy from 'util/sortedBy';

import type {ProteinPredictionJobsQuery} from './__generated/ProteinPredictionJobsQuery';

export const TERMINAL_PREDICTION_JOB_STATUSES = new Set<PredictionJobStatus>([
  PredictionJobStatus.COMPLETED,
  PredictionJobStatus.FAILED,
]);

/**
 * Builds the finalized 3D structures for a protein entity directly from its prediction jobs.
 *
 * A completed job's standardized output is the predicted structure (mmCIF), so its signed download
 * URL renders straight in the Mol* viewer without loading each chain entity's versions. Jobs without
 * a standardized output are omitted because they have no viewable structure.
 */
export function getCompletedStructuresFromJobs(
  jobs: ReadonlyArray<ProteinPredictionJobsQuery['proteinVersionPredictionJobs'][number]>
): Array<PreSignedStructureInfo> {
  const structures = jobs
    .filter(
      (job) => job.status === PredictionJobStatus.COMPLETED && job.standardizedOutputDownloadUrl != null
    )
    .map((job): PreSignedStructureInfo => {
      // The schema types `modelVersion` as nullable because the underlying column is nullable, but a
      // prediction batch always records the version it ran, so assert rather than propagating a null.
      const {modelVersion} = job.predictionBatch;
      assert(modelVersion != null, 'Prediction batch is missing a model version');
      return {
        preSigned: true,
        structureFile: {
          id: job.id,
          url: job.standardizedOutputDownloadUrl!,
          previewType: EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER as const,
          availablePreviewTypes: [EXTERNAL_FILE_TYPE.MOLSTAR_MMCIF_VIEWER],
        },
        model: job.modelName,
        modelVersion,
        createdAt: job.createdAt,
        coordinateStructureId: job.id,
        // The query for protein prediction jobs only returns those which match the current aa seq versions
        // present in the protein, so it's never out of date.
        structureFileOutOfDate: false,
        componentIds: job.componentApiIds,
      };
    });

  return sortedBy(structures, [(structure) => structure.createdAt]);
}
